
import React, { useState, useEffect } from 'react';
import { XMarkIcon, CloudArrowUpIcon, CheckIcon, ExclamationTriangleIcon } from '../../../components/Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../../firebase/firebaseConfig';
import { useInmobiliariaCsvProcessor } from '../../../hooks/useInmobiliariaCsvProcessor';
import { CsvRowSelector } from './CsvRowSelector';

interface InmobiliariaInfo {
    id: string;
    name: string;
    subKeys: string[];
}

interface AdminInmobiliariaCsvModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialDocId: string;
    existingDocs: InmobiliariaInfo[];
}

export const AdminInmobiliariaCsvModal: React.FC<AdminInmobiliariaCsvModalProps> = ({ isOpen, onClose, initialDocId, existingDocs }) => {
    const [targetDocId, setTargetDocId] = useState(initialDocId);
    const [globalSubKey, setGlobalSubKey] = useState<string>('');
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const { 
        file, 
        data, 
        headers, 
        mapping, 
        isProcessing, 
        handleFileChange, 
        handleMappingChange, 
        processFile 
    } = useInmobiliariaCsvProcessor();

    useEffect(() => {
        if (data.length > 0) {
            setSelectedRows(new Set(data.map((_, i) => i)));
        }
    }, [data]);

    const targetDoc = existingDocs.find(d => d.id === targetDocId);
    const isAggregator = targetDocId === '183150';

    const handleProcess = async () => {
        const rowsToProcess = data.filter((_, index) => selectedRows.has(index));
        if (rowsToProcess.length === 0) return alert("Selecciona al menos una fila para procesar.");
        
        const success = await processFile(targetDocId, globalSubKey, rowsToProcess);
        if (success) {
            alert("Procesamiento completado con éxito.");
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center">
                        <CloudArrowUpIcon className="w-5 h-5 mr-2 text-sky-600" />
                        Carga Masiva de Configuraciones
                    </h3>
                    <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full transition-colors">
                        <XMarkIcon className="w-6 h-6 text-gray-400" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <section className="space-y-4">
                            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">1. Configuración de Destino</h4>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Inmobiliaria Principal</label>
                                <select 
                                    value={targetDocId} 
                                    onChange={(e) => { setTargetDocId(e.target.value); setGlobalSubKey(''); }}
                                    className="w-full border-gray-300 rounded-lg text-sm focus:ring-sky-500 focus:border-sky-500"
                                >
                                    {existingDocs.map(doc => (
                                        <option key={doc.id} value={doc.id}>{doc.id} - {doc.name}</option>
                                    ))}
                                </select>
                            </div>

                            {isAggregator && targetDoc && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Sub-Inmobiliaria (Global)</label>
                                    <select 
                                        value={globalSubKey} 
                                        onChange={(e) => setGlobalSubKey(e.target.value)}
                                        className="w-full border-gray-300 rounded-lg text-sm focus:ring-sky-500 focus:border-sky-500"
                                    >
                                        <option value="">-- Seleccionar Sub-Inmobiliaria --</option>
                                        {targetDoc.subKeys.map(key => (
                                            <option key={key} value={key}>{key}</option>
                                        ))}
                                    </select>
                                    <p className="text-[10px] text-gray-400 mt-1 italic">
                                        * Si se deja vacío, se buscará la columna 'SUB_INMOBILIARIA' en el CSV.
                                    </p>
                                </div>
                            )}
                        </section>

                        <section className="space-y-4">
                            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">2. Archivo Fuente</h4>
                            <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-sky-300 transition-colors">
                                <input 
                                    type="file" 
                                    accept=".csv,.xlsx,.xls" 
                                    onChange={handleFileChange}
                                    className="hidden" 
                                    id="csv-upload"
                                />
                                <label htmlFor="csv-upload" className="cursor-pointer flex flex-col items-center">
                                    <CloudArrowUpIcon className="w-10 h-10 text-gray-300 mb-2" />
                                    <span className="text-sm font-medium text-sky-600">{file ? file.name : 'Seleccionar archivo CSV o Excel'}</span>
                                    <span className="text-xs text-gray-400 mt-1">Máximo 5MB</span>
                                </label>
                            </div>
                        </section>
                    </div>

                    {data.length > 0 && (
                        <div className="space-y-6 animate-fade-in">
                            <section className="space-y-3">
                                <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">3. Mapeo de Columnas</h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    {Object.keys(mapping).map(field => (
                                        <div key={field}>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{field}</label>
                                            <select 
                                                value={mapping[field as keyof typeof mapping]} 
                                                onChange={(e) => handleMappingChange(field as any, e.target.value)}
                                                className="w-full border-gray-200 rounded-md text-xs py-1"
                                            >
                                                <option value="">(Ignorar)</option>
                                                {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            <section className="space-y-3">
                                <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">4. Selección de Filas ({selectedRows.size} seleccionadas)</h4>
                                <CsvRowSelector 
                                    data={data} 
                                    selectedRows={selectedRows} 
                                    onToggleRow={(idx) => {
                                        const next = new Set(selectedRows);
                                        if (next.has(idx)) next.delete(idx);
                                        else next.add(idx);
                                        setSelectedRows(next);
                                    }}
                                    onSelectAll={(all) => setSelectedRows(all ? new Set(data.map((_, i) => i)) : new Set())}
                                />
                            </section>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">
                    <button onClick={onClose} className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors">
                        Cancelar
                    </button>
                    <button 
                        onClick={handleProcess}
                        disabled={isProcessing || !file || data.length === 0}
                        className="px-6 py-2 text-white font-bold rounded-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:shadow-none transition-all flex items-center"
                        style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                    >
                        {isProcessing ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Procesando...
                            </>
                        ) : (
                            <>
                                <CheckIcon className="w-5 h-5 mr-2" />
                                Iniciar Carga
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
